import React, { useState } from 'react'
import PropTypes from 'prop-types'
import RpModal from './rp-modal'
import Button from '../Button'
import DeleteConfirmation from '../DeleteConfirmation'

import styled from '@emotion/styled'
import {
  underlineFocus,
  FocusSpan,
} from 'portal-modules/common/components/focus-styles'

const StyledButton = styled(Button)`
  margin-top: 16px;

  :first-of-type {
    margin-top: 32px;
  }
`

const StyledCancel = styled('button')`
  width: 100%;
  background: none;
  border: none;
  color: ${({ theme }) => theme.text.white};
  display: block;
  font-size: 1rem;
  font-weight: bold;
  padding: 16px;

  ${underlineFocus}
`

const ActionModal = props => {
  const [isConfirming, setIsConfirming] = useState(false)

  const cancelDelete = () => {
    setIsConfirming(false)
    props.closeOnDeleteCancel && props.onClose()
  }

  const handleClose = () => {
    cancelDelete()
    props.onClose()
  }

  const confirmDelete = () => {
    props.onDelete()
    handleClose()
  }

  const showConfirmation = () => {
    setIsConfirming(true)
  }

  const handleCancel = () => {
    const { onCancel } = props
    if (typeof onCancel === 'function') onCancel()
    handleClose()
  }

  const {
    children,
    onClose,
    onDelete,
    render,
    renderConfirmation,
    cancelText,
    isHideCancel,
    cancelStyle,
    ...rest
  } = props
  return props.show ? (
    <RpModal
      outsideContent={
        isHideCancel || isConfirming ? (
          undefined
        ) : (
          <StyledCancel type='button' css={cancelStyle} onClick={handleCancel}>
            <FocusSpan>{cancelText || __('Cancel')}</FocusSpan>
          </StyledCancel>
        )
      }
      onClose={handleClose}
      {...rest}
    >
      {isConfirming ? (
        <DeleteConfirmation onCancel={cancelDelete} onDelete={confirmDelete}>
          {renderConfirmation()}
        </DeleteConfirmation>
      ) : (
        (render || children)({
          showConfirmation: showConfirmation,
        })
      )}
    </RpModal>
  ) : null
}

ActionModal.propTypes = {
  /** Children as a function. Gets passed a function to confirm a delete action. */
  children: PropTypes.func,
  /** Hook to the cancel event when needed */
  onCancel: PropTypes.func,
  /** Hook to the close event. Usually provided by ModalControl. */
  onClose: PropTypes.func.isRequired,
  /** Hook to delete action. Gets wired up in confirmDelete, which is passed to `render` or `children`. */
  onDelete: PropTypes.func,
  /** Gets passed a function to confirm a delete action. */
  render: PropTypes.func,
  /** Renders the confirmation content. Has all of the actions already wired up. */
  renderConfirmation: PropTypes.func,
  /** Toggle the visibility of the modal */
  show: PropTypes.bool,
  /** Modal title */
  title: PropTypes.string,
  /** Escape hatch when needing to make minor additions/adjustments to the container div for the modal */
  wrapperClass: PropTypes.string,
  closeOnDeleteCancel: PropTypes.bool,
  cancelText: PropTypes.string,
  isHideCancel: PropTypes.bool,
  cancelStyle: PropTypes.string,
}

ActionModal.Button = ({ children, ...props }) => (
  <Button type='button' appearance='primary' size='large' {...props}>
    {children || __('Save')}
  </Button>
)

ActionModal.Button.propTypes = {
  children: PropTypes.func,
}

ActionModal.DeleteButton = ({ children, ...props }) => (
  <Button appearance='secondary' {...props} size='large'>
    {children || __('Delete')}
  </Button>
)

ActionModal.DeleteButton.propTypes = {
  children: PropTypes.func,
}

export default ActionModal
